import type { FC, FormEvent, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { apiFetch } from '@/lib/client'
import PageShell from '@/components/generals/page-shell'
import { useSite } from '@/components/landing/site-provider'

import {
    buttonPrimary,
    cardClassName,
    Field,
    inputClassName,
    Notice,
    safeNext,
    Spinner
} from '@/components/generals/ui'

const LoginPage: FC = (): ReactNode => {
    const router = useRouter()
    const { refreshUser } = useSite()

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')

    const next = safeNext(router.query.next)
    const fromBag = router.query.alasan === 'keranjang'

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (submitting) return

        if (!email.trim() || !password) {
            setError('Isi email dan password.')
            return
        }

        setSubmitting(true)
        setError('')

        const result = await apiFetch('/api/auth/login', { body: { email: email.trim(), password } })

        if (!result.ok) {
            setSubmitting(false)
            setError(result.message)
            return
        }

        await refreshUser()
        await router.replace(next)
    }

    return (
        <PageShell title='Masuk' description='Masuk ke akun Hajime untuk membeli dan mengunduh ebook bahasa Jepang.' width='narrow'>
            <h1 className='font-heading font-soft text-4xl font-medium tracking-[-0.03em]'>Masuk</h1>

            <p className='mt-2 text-cream/65'>
                Belum punya akun? <Link href={`/daftar?next=${encodeURIComponent(next)}${fromBag ? '&alasan=keranjang' : ''}`} className='font-semibold text-crema underline-offset-4 hover:underline'>Daftar gratis</Link>
            </p>

            {fromBag && (
                <Notice tone='info' className='mt-6'>
                    Masuk atau daftar dulu untuk membeli. Ebook yang kamu pilih otomatis masuk ke keranjang setelah kamu masuk.
                </Notice>
            )}

            <form noValidate onSubmit={onSubmit} className={`${cardClassName} mt-8 flex flex-col gap-4`}>
                <Field label='Email' htmlFor='login-email'>
                    <input
                        id='login-email'
                        type='email'
                        inputMode='email'
                        autoComplete='email'
                        required
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        className={inputClassName}
                    />
                </Field>

                <Field label='Password' htmlFor='login-password'>
                    <input
                        id='login-password'
                        type='password'
                        autoComplete='current-password'
                        required
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        className={inputClassName}
                    />
                </Field>

                <Link href='/lupa-password' className='w-fit text-sm text-cream/65 underline-offset-4 hover:text-cream hover:underline'>
                    Lupa password?
                </Link>

                {error && <Notice tone='error'>{error}</Notice>}

                <button type='submit' disabled={submitting} className={buttonPrimary}>
                    {submitting && <Spinner />}
                    {submitting ? 'Memeriksa' : 'Masuk'}
                </button>
            </form>
        </PageShell>
    )
}

/* Already signed in: go straight on. */
export const getServerSideProps: GetServerSideProps = async (context) => {
    const { getSessionUser } = await import('@/lib/auth')
    const user = await getSessionUser(context.req)

    if (user) return { redirect: { destination: safeNext(context.query.next), permanent: false } }

    return { props: { user: null } }
}

export default LoginPage
